import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import LightGestion from 'components/headers/lightGestion';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import { Button, Modal, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import './Caisse.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';

const Caisse = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [query, setQuery] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [clients, setClients] = useState([]);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [paymentType, setPaymentType] = useState('');
  // Cheque-specific fields
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeImage, setChequeImage] = useState(null); // base64 data URL
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', phone: '', address: '', Entreprise: '' });
  const [addingClient, setAddingClient] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const invoiceRef = useRef(null);
  // Société (company) info for invoice header
  const [company, setCompany] = useState(null);

  const toAbsImageUrl = (url) => {
    if (!url) return '';
    if (typeof url !== 'string') return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const base = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:8000';
    let normalized = url.replace(/^\/+/, '');
    if (normalized.startsWith('storage/')) return `${base}/${normalized}`;
    if (normalized.startsWith('caisse/')) return `${base}/storage/${normalized}`;
    if (normalized.startsWith('images/') || normalized.startsWith('uploads/')) return `${base}/${normalized}`;
    return `${base}/${normalized}`;
  };

  const normalizeText = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'number') return String(val);
    if (typeof val === 'object') {
      return val.fr || val.en || val.ar || Object.values(val)[0] || '';
    }
    return '';
  };

  const currency = useCallback((v) => `${Number(v || 0).toFixed(2)} ${t('stock.currency','MAD')}`,[t]);

  // Normalize string for search: lowercase + strip diacritics
  const toSearch = (val) => {
    const s = normalizeText(val).toLowerCase();
    // Remove accents/diacritics for accent-insensitive search
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };

  const getCategory = (raw) => {
    if (!raw) return '';
    return (
      raw.Category || raw.category || raw.CategoryAdmin || raw.categorie || raw.CategoryName || raw.category_admin || ''
    );
  };

  const getPriceForRaw = (raw, tariff) => {
    if (!raw) return 0;
    // common keys
    const detail = Number(
      raw.PrixDetail || raw.prixDetail || raw.retail_price || raw.price || raw.prix_detail || raw.prix_detaille || raw.retail || raw.prix || 0
    );
    const gros = Number(
      raw.PrixGros || raw.prixGros || raw.wholesale_price || raw.price_gros || raw.prix_gros || raw.gros || raw.wholesale || 0
    );
    const carton = Number(
      raw.PrixCarton || raw.prixCarton || raw.carton_price || raw.prix_carton || raw.price_carton || raw.carton || 0
    );
    if (tariff === 'G') return gros || detail;
    if (tariff === 'C') return carton || detail;
    return detail || gros || carton || 0;
  };

  // Try to get a stable reference/barcode/code for uniqueness
  const getRefCode = (raw) => {
    if (!raw) return '';
    return String(
      raw.Ref || raw.ref || raw.Barcode || raw.barcode || raw.code_barre || raw.codeBarre || raw.sku || raw.SKU || ''
    );
  };
 // ✅ FIXED: fetchProducts now keeps numeric ID from backend
  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/products', config);
      const rawContainer = res.data || {};
      const raw = Array.isArray(rawContainer)
        ? rawContainer
        : (rawContainer.data?.data || rawContainer.data || rawContainer.products || rawContainer.items || rawContainer.produits || []);
      const data = (raw || []).map((p, idx) => {
        const rawName = p.name || p.Name || p.nom || p.NameTranslation || '';
        // ✅ numeric ID from backend if exists
        const backendId = p.ID || p.id || p.ProductID || p.product_id;
        const idCandidate = backendId ?? `${getRefCode(p)}|${idx}`;
        return {
          id: String(idCandidate),
          backendId: backendId || null, // store real numeric ID
          name: normalizeText(rawName),
          stock: p.StockQuantity || p.stock_quantity || p.stock || 0,
          _raw: p
        };
      });
      setProducts(data);
    } catch (e) {
      console.error(e);
      Swal.fire(t('caisse.alerts.error','Erreur'), t('caisse.alerts.fetchProductsFailed','Impossible de récupérer les produits'));
    }
  }, [t]);


  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const resp = await axios.get('https://plateformewmg.cloud/api/Gestion/category-admin-options', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (resp.status === 200) {
        const list = Array.isArray(resp.data)
          ? resp.data
          : resp.data.categories_admin || resp.data.data || resp.data.categories || [];
        const cats = (list || []).map(c => (typeof c === 'string' ? c : (c.name || c.label || c.title || ''))).filter(Boolean);
        setCategories(['ALL', ...Array.from(new Set(cats))]);
      } else {
        console.error('Unexpected response structure:', resp.data);
      }
    } catch (e) {
      console.error('Failed to fetch category options', e);
    }
  }, []);


 const fetchClients = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/clientgestions', {
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      });
      const raw = Array.isArray(res.data) ? res.data : (res.data.data || res.data.clients || []);
      const data = (raw || []).map(r => ({
        id: r.id || r.client_id || r._id,
        name: normalizeText(r.name || r.Nom || r.nom || r.label || '')
      }));
      setClients(data);
    } catch (e) {
      console.error(e);
    }
  }, []);


  useEffect(() => { fetchProducts(); fetchClients(); fetchCategories(); }, [fetchProducts, fetchClients, fetchCategories]);

  // Fetch Société (company) for invoice header
  useEffect(() => {
    const run = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        const res = await axios.get('https://plateformewmg.cloud/api/Gestion/societes', cfg);
        const s = res.data?.data || res.data?.societe || res.data || {};
        const mapped = {
          Nom: s.Nom || s.name || s.nom || '',
          Telephone: s.Telephone || s.phone || s.telephone || '',
          Email: s.Email || s.email || '',
          SiteWeb: s.SiteWeb || s.website || s.siteweb || '',
          Address: s.Address || s.address || s.adresse || '',
          ICE: s.ICE || s.ice || '',
          RC: s.RC || s.rc || '',
          IF: s.IF || s.if || '',
          TP: s.TP || s.tp || '',
          logo: toAbsImageUrl(s.logo || s.Logo || s.logo_url || '')
        };
        setCompany(mapped);
      } catch (err) {
        if (err?.response?.status === 404) {
          console.info('Société introuvable - aucune configuration trouvée. Cette étape est facultative.');
        } else {
          console.warn('Société introuvable ou non configurée', err);
        }
      }
    };
    run();
  }, []);

  // Reset cheque fields when payment type changes away from 'cheque'
  useEffect(() => {
    if (paymentType !== 'cheque') {
      setChequeNumber('');
      setChequeImage(null);
    }
  }, [paymentType]);

  const visibleProducts = products.filter(p => {
    // category filter
    if (selectedCategory && selectedCategory !== 'ALL') {
      const cat = String(getCategory(p._raw) || '').toLowerCase();
      if (!cat.includes(String(selectedCategory || '').toLowerCase())) return false;
    }

    // Build normalized fields for search
    const nameNorm = toSearch(p.name || '');
    const refNorm = toSearch((p._raw && (p._raw.Ref || p._raw.ref || p._raw.Barcode || p._raw.barcode || p._raw.code_barre || p._raw.codeBarre)) || '');
    const qRef = toSearch(query);
    const qName = toSearch(nameQuery);

    // If a name query is provided, enforce it against product name only
    if (qName && !nameNorm.includes(qName)) return false;

    // If a barcode/reference query is provided, allow match against name OR ref
    if (qRef && !(nameNorm.includes(qRef) || refNorm.includes(qRef))) return false;

    return true;
  });

    const addToCart = (product) => {
    setCart(prev => {
      const pid = String(product.id);
      const existing = prev.find(i => String(i.id) === pid);
      const defaultPrice = getPriceForRaw(product._raw, 'D');
      if (existing) return prev.map(i => String(i.id) === pid ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { id: pid, backendId: product.backendId, name: product.name, price: defaultPrice, qty: 1, tariff: 'D', _raw: product._raw }];
    });
  };

  const updateQty = (id, qty) => setCart(prev => prev.map(i => i.id === id ? { ...i, qty: Number(qty) } : i));
  const updatePrice = (id, price) => setCart(prev => prev.map(i => i.id === id ? { ...i, price: Number(price) } : i));
  const changeTariff = (id, tariff) => setCart(prev => prev.map(i => i.id === id ? ({ ...i, tariff, price: getPriceForRaw(i._raw, tariff) }) : i));
  const removeItem = (id) => setCart(prev => prev.filter(i => i.id !== id));
  const total = cart.reduce((s,i) => s + (Number(i.price || 0) * (Number(i.qty) || 0)), 0);

  const handleValidate = async () => {
    if (cart.length === 0) return Swal.fire(t('caisse.alerts.error','Erreur'), t('caisse.cart.empty','Le panier est vide'));
    setPaymentType('');
    setShowPayment(true);
  };

   const submitSale = async () => {
    try {
      const token = localStorage.getItem('authToken');
        const payload = {
        client_id: selectedClient?.id || null,
        items: cart.map(i => ({
          product_id: i.backendId || i._raw?.ID || i._raw?.id || i._raw?.ProductID, // ✅ safe fallback
          qty: Number(i.qty),
          price: Number(i.price),
          tariff: i.tariff
        })),
        total: total,
        payment_amount: Number(paymentAmount || 0),
          payment_type: paymentType || 'cash'
      };
      console.log("Submitting payload:", payload);

      const chequeMeta = {
        paymentType,
        chequeNumber,
        hasChequeImage: Boolean(chequeImage),
      };
      if (paymentType === 'cheque') {
        payload.NumCheque = chequeNumber || null;
        payload.ChequeImage = chequeImage || null;
      }
      console.log('Submitting payload:', payload);
      console.log('Cheque meta:', chequeMeta);

      const res = await axios.post('https://plateformewmg.cloud/api/Gestion/sales/create', payload, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      const created = res.data?.data || res.data?.sale || res.data;
      const saleId = created?.id || created?.sale_id || created?.ID || Math.floor(Math.random() * 1e6);

      // Build invoice object from current state + response identifiers
      const inv = {
        id: saleId,
        date: new Date().toISOString(),
        client: {
          name: selectedClient?.name || 'Client de passage',
          ice: selectedClient?.ice || '',
          address: selectedClient?.address || '',
          Entreprise: selectedClient?.Entreprise || ''
        },
        items: cart.map(i => ({
          name: i.name,
          qty: Number(i.qty),
          price: Number(i.price),
          total: Number(i.price) * Number(i.qty)
        })),
        payment: {
          type: paymentType || 'cash',
          amount: Number(paymentAmount || 0)
        },
        totals: {
          subtotal: cart.reduce((s,i)=> s + Number(i.price)*Number(i.qty), 0),
          total: total,
          credit: Math.max(0, total - Number(paymentAmount || 0))
        }
      };

      Swal.fire({
        title: t('caisse.alerts.success','Succès'),
        text: t('caisse.alerts.saleSaved','Vente enregistrée'),
        icon: 'success',
        confirmButtonText: t('caisse.invoice.download','Télécharger PDF'),
        showCancelButton: true,
        cancelButtonText: t('caisse.invoice.close','Fermer')
      }).then((result) => {
        if (result.isConfirmed) {
          setInvoice(inv);
          setShowInvoice(true);
          // delay to render modal then auto-generate
          setTimeout(() => handleDownloadInvoice(inv), 300);
        }
      });
      setCart([]);
      setShowPayment(false);
      setPaymentAmount('');
      setChequeNumber('');
      setChequeImage(null);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || e?.message || '';
      if (typeof msg === 'string' && msg.includes("Unknown column") && msg.includes('NumCheque')) {
        Swal.fire(t('caisse.alerts.error','Erreur'), t('caisse.alerts.chequeFieldsUnsupported',"Le serveur n'accepte pas encore les champs de chèque (NumCheque, image). La vente a été enregistrée sans ces détails."), 'error');
      } else {
        const details = typeof e?.response?.data === 'string' ? e.response.data : (e?.response?.data?.message || e.message || 'Erreur inconnue');
        Swal.fire(t('caisse.alerts.error','Erreur'), String(details));
      }
    }
  };

  const handleDownloadInvoice = async (inv = invoice) => {
    try {
      const node = invoiceRef.current;
      if (!node) return;
  const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let y = 0;
      pdf.addImage(imgData, 'PNG', 0, y, imgWidth, imgHeight);
      // If content exceeds one page, add pages
      let remaining = imgHeight - pageHeight;
      while (remaining > 0) {
        y = remaining * -1; // move up for next slice
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, y, imgWidth, imgHeight);
        remaining -= pageHeight;
      }
      pdf.save(`facture_${inv?.id || 'sale'}.pdf`);
    } catch (err) {
      console.error('PDF generation failed', err);
      Swal.fire(t('caisse.alerts.error','Erreur'), t('caisse.alerts.pdfFailed','Génération de la facture impossible'));
    }
  };


  return (
    <AnimationRevealPage>
      <LightGestion />
      <div className="container-fluid px-0 py-0">
        <div className="d-flex" style={{ gap: 16 ,marginTop:"-50px"}}>
          {/* LEFT: scanner / filters / products */}
          <div style={{ flex: 1 }}>
            <div className="card shadow-sm mb-3">
              <div className="card-body">
                <h6 className="text-secondary mb-2">{t('caisse.scan.title','Scanner Code-Barres')}</h6>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <input className="form-control" placeholder={t('caisse.scan.placeholder','Scannez ou tapez le code-barres...')} value={query} onChange={(e) => setQuery(e.target.value)} />
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge rounded-circle" style={{ width: 12, height: 12, background: '#10B981' }}></span>
                  </div>
                </div>

                <h6 className="text-secondary mt-3 mb-2">{t('caisse.search.title','Rechercher un produit')}</h6>
                <input className="form-control" placeholder={t('caisse.search.placeholder','Rechercher par nom de produit...')} value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} />

                <div className="mt-3">
                  <label className="text-secondary small">{t('caisse.categories.title','Catégories')}</label>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {(categories || []).map(cat => {
                      const active = selectedCategory === cat;
                      return (
                        <button
                          key={cat}
                          onClick={() => setSelectedCategory(cat)}
                          className={`badge rounded-pill ${active ? 'bg-primary text-white' : 'bg-light text-secondary border'}`}
                          style={{ padding: '8px 12px', fontSize: 14, cursor: 'pointer' }}
                        >
                          {cat === 'ALL' ? t('caisse.categories.all','Toutes') : cat}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3">
              {visibleProducts.slice(0, 12).map(p => (
                <div key={p.id} className="col-12 col-sm-6 col-md-4">
                  <div className="card h-100 shadow-sm" style={{ cursor: 'pointer' }} onClick={() => addToCart(p)}>
                    <div className="card-body text-center">
                      <div className="d-flex align-items-center justify-content-center mb-2" style={{ height: 110 }}>
                        <img src={(p._raw && (p._raw.image || p._raw.image_url || p._raw.img)) || '/images/placeholder.png'} alt="img" style={{ maxHeight: 100, maxWidth: '100%' }} />
                      </div>
                      <div className="fw-bold">{p.name}</div>
                      <div className="mt-2 text-primary fw-bold">
                        {currency(getPriceForRaw(p._raw, 'D'))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: client + cart */}
          <div style={{ width: 560, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="card shadow-sm">
              <div className="card-body d-flex align-items-center gap-2">
                <Form.Control list="client-list-caisse" placeholder={t('caisse.client.placeholder','Client')} value={selectedClient?.name || ''} onChange={(e) => {
                  const name = e.target.value;
                  const found = clients.find(x => (x.name || '').toLowerCase() === name.toLowerCase());
                  if (found) setSelectedClient(found); else setSelectedClient({ name });
                }} />
                <datalist id="client-list-caisse">{clients.map(c => (<option key={c.id} value={c.name} />))}</datalist>
                <Button variant="primary" onClick={() => { setNewClient({ name: selectedClient?.name || '', phone: '', address: '', Entreprise: '' }); setShowAddClient(true); }}>+</Button>
                <Button variant="outline-primary">{t('caisse.client.walkIn','Client de passage')}</Button>
              </div>
            </div>

            <div className="card shadow-sm" style={{ minHeight: 400 }}>
              <div className="card-body">
                <div className="row cart-header fw-bold rounded px-2 py-2 mb-2" style={{ marginLeft: 0, marginRight: 0 }}>
                  <div className="col">{t('caisse.cart.headers.product','PRODUIT')}</div>
                  <div className="col-2 text-center">{t('caisse.cart.headers.qty','QTÉ')}</div>
                  <div className="col-3 text-center">{t('caisse.cart.headers.unitPrice','PRIX U.')}</div>
                  <div className="col-3 text-center">{t('caisse.cart.headers.total','TOTAL')}</div>
                  <div className="col-1"></div>
                </div>

                <div style={{ minHeight: 260 }}>
                  {cart.length === 0 && <div className="text-muted p-3">{t('caisse.cart.empty','Le panier est vide')}</div>}
                  {cart.map(item => (
                    <div key={item.id} className="row align-items-center py-3 cart-row" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="col">
                        <div className="fw-bold text-truncate" title={item.name}>{item.name}</div>
                        <div className="tariff mt-2">
                          <span className="tariff-label">{t('caisse.cart.tariff','Tarif:')}</span>
                          <button className={`tariff-btn ${item.tariff === 'D' ? 'active' : ''}`} onClick={() => changeTariff(item.id, 'D')}>D</button>
                          <button className={`tariff-btn ${item.tariff === 'G' ? 'active' : ''}`} onClick={() => changeTariff(item.id, 'G')}>G</button>
                          <button className={`tariff-btn ${item.tariff === 'C' ? 'active' : ''}`} onClick={() => changeTariff(item.id, 'C')}>C</button>
                        </div>
                      </div>
                      <div className="col-2 text-center">
                        <input className="qty-input" type="number" value={item.qty} min={1} onChange={(e) => updateQty(item.id, e.target.value)} />
                      </div>
                      <div className="col-3 text-center">
                        <input className="price-input" type="number" value={Number(item.price).toFixed(2)} onChange={(e) => updatePrice(item.id, e.target.value)} />
                      </div>
                      <div className="col-3 text-center fw-bold">{currency(Number(item.price || 0) * Number(item.qty || 0))}</div>
                      <div className="col-1 text-center">
                        <button type="button" className="remove-btn" onClick={() => removeItem(item.id)}>X</button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="fw-bold fs-4">{t('caisse.cart.totalAmount','Montant Total :')}</div>
                  <div className="fw-bold fs-4">{currency(total)}</div>
                </div>

                <div className="d-flex gap-2 justify-content-end mt-3">
                  <Button variant="danger" onClick={() => setCart([])}>{t('caisse.cart.cancel','Annuler la Vente')}</Button>
                  <Button variant="success" onClick={handleValidate}>{t('caisse.cart.validate','Valider le Paiement')}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Invoice Modal */}
      <Modal show={showInvoice} onHide={() => setShowInvoice(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('caisse.invoice.title','Facture')} #{invoice?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div ref={invoiceRef} style={{ background: 'var(--card-bg)', padding: 16 }}>
            {/* Société header */}
            {company && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {company.logo ? (
                    <img src={company.logo} alt="Logo société" style={{ height: 50, objectFit: 'contain' }} />
                  ) : null}
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 18 }}>{company.Nom || ''}</div>
                    {company.Address ? <div style={{ fontSize: 12, color: '#6b7280' }}>{company.Address}</div> : null}
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {company.Telephone ? <>{t('caisse.invoice.phone','Tél:')} {company.Telephone}</> : null}
                      {company.Email ? <> {company.Telephone ? ' | ' : ''}{t('caisse.invoice.email','Email:')} {company.Email}</> : null}
                    </div>
                    {company.SiteWeb ? <div style={{ fontSize: 12, color: '#6b7280' }}>{company.SiteWeb}</div> : null}
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {company.ICE ? <>{t('caisse.invoice.ice','ICE:')} {company.ICE} </> : null}
                      {company.RC ? <>{t('caisse.invoice.rc','RC:')} {company.RC} </> : null}
                      {company.IF ? <>{t('caisse.invoice.if','IF:')} {company.IF} </> : null}
                      {company.TP ? <>{t('caisse.invoice.tp','TP:')} {company.TP}</> : null}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{t('caisse.invoice.invoice','Facture')}</div>
                <div style={{ color: '#6b7280' }}>{t('caisse.invoice.date','Date:')} {new Date(invoice?.date || Date.now()).toLocaleString()}</div>
                <div style={{ color: '#6b7280' }}>{t('caisse.invoice.number','N°:')} {invoice?.id}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700 }}>{t('caisse.invoice.client','Client')}</div>
                <div>{invoice?.client?.name}</div>
                {invoice?.client?.ice ? <div>{t('caisse.invoice.ice','ICE:')} {invoice.client.ice}</div> : null}
                {invoice?.client?.address ? <div>{invoice.client.address}</div> : null}
              </div>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('caisse.invoice.table.product','Produit')}</th>
                  <th className="text-end">{t('caisse.invoice.table.qty','Qté')}</th>
                  <th className="text-end">{t('caisse.invoice.table.unitPrice','Prix U.')}</th>
                  <th className="text-end">{t('caisse.invoice.table.total','Total')}</th>
                </tr>
              </thead>
              <tbody>
                {(invoice?.items || []).map((it, idx) => (
                  <tr key={idx}>
                    <td>{it.name}</td>
                    <td className="text-end">{it.qty}</td>
                    <td className="text-end">{currency(Number(it.price))}</td>
                    <td className="text-end">{currency(Number(it.total))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24 }}>
              <div>
                <div className="d-flex justify-content-between" style={{ minWidth: 260 }}>
                  <div>{t('caisse.invoice.subtotal','Sous-total')}</div>
                  <div>{currency(invoice?.totals?.subtotal || 0)}</div>
                </div>
                <div className="d-flex justify-content-between" style={{ minWidth: 260 }}>
                  <div>{t('caisse.invoice.paidAmount','Montant payé')} ({invoice?.payment?.type})</div>
                  <div>{currency(invoice?.payment?.amount || 0)}</div>
                </div>
                <div className="d-flex justify-content-between fw-bold" style={{ minWidth: 260 }}>
                  <div>{t('caisse.invoice.total','Total')}</div>
                  <div>{currency(invoice?.totals?.total || 0)}</div>
                </div>
                {invoice?.totals?.credit > 0 && (
                  <div className="d-flex justify-content-between" style={{ minWidth: 260, color: '#f97316' }}>
                    <div>{t('caisse.invoice.credit','Crédit')}</div>
                    <div>{currency(invoice?.totals?.credit || 0)}</div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>{t('caisse.invoice.thanks','Merci pour votre achat.')}</div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInvoice(false)}>{t('caisse.invoice.close','Fermer')}</Button>
          <Button variant="primary" onClick={() => handleDownloadInvoice()}>{t('caisse.invoice.download','Télécharger PDF')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPayment} onHide={() => setShowPayment(false)} size={paymentType ? 'lg' : 'md'}>
        <Modal.Header closeButton>
          <Modal.Title>{t('caisse.payment.title','Choisissez le type de paiement')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!paymentType ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Button variant="success" style={{ padding: '18px', fontSize: 18 }} onClick={() => setPaymentType('cash')}>💰 {t('caisse.payment.cash','Paiement Comptant')}</Button>
              <Button variant="primary" style={{ padding: '18px', fontSize: 18 }} onClick={() => setPaymentType('cheque')}>📄 {t('caisse.payment.cheque','Paiement Chèque')}</Button>
              <Button variant="warning" style={{ padding: '18px', fontSize: 18, color: 'white' }} onClick={() => setPaymentType('credit')}>💳 {t('caisse.payment.credit','Crédit / Partiel')}</Button>
            </div>
          ) : (
            <Form>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 700 }}>{t('caisse.payment.totalAmount','Montant Total :')}</div>
                <div style={{ fontWeight: 700 }}>{currency(total)}</div>
              </div>
              <Form.Group>
                <Form.Label>{t('caisse.payment.client','Client')}</Form.Label>
                <Form.Control list="client-list-caisse" placeholder={t('caisse.payment.clientPlaceholder','Sélectionner un client...')} value={selectedClient?.name || ''} onChange={(e) => {
                  const name = e.target.value;
                  const found = clients.find(x => (x.name || '').toLowerCase() === name.toLowerCase());
                  if (found) setSelectedClient(found); else setSelectedClient({ name });
                }} />
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Label>{t('caisse.payment.paidAmount','Montant payé')}</Form.Label>
                <Form.Control type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
              </Form.Group>
              {paymentType === 'cheque' && (
                <div className="mt-3" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <div className="fw-bold mb-2">{t('caisse.payment.chequeDetails.title','Détails du chèque')}</div>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('caisse.payment.chequeDetails.number','Numéro du chèque')}</Form.Label>
                    <Form.Control type="text" placeholder={t('caisse.payment.chequeDetails.numberPlaceholder','Ex: 123456')} value={chequeNumber} onChange={(e) => setChequeNumber(e.target.value)} />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>{t('caisse.payment.chequeDetails.image','Image du chèque (facultatif)')}</Form.Label>
                    <Form.Control type="file" accept="image/*" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) { setChequeImage(null); return; }
                      const reader = new FileReader();
                      reader.onload = () => setChequeImage(reader.result);
                      reader.readAsDataURL(file);
                    }} />
                  </Form.Group>
                  {chequeImage && (
                    <div className="mt-2" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <img src={chequeImage} alt="Chèque" style={{ maxHeight: 80, borderRadius: 6, border: '1px solid var(--border)' }} />
                      <Button variant="outline-danger" size="sm" onClick={() => setChequeImage(null)}>{t('caisse.payment.chequeDetails.remove','Retirer')}</Button>
                    </div>
                  )}
                </div>
              )}
              {paymentType === 'credit' && (
                <div style={{ marginTop: 12 }}>
                  <div>{t('caisse.payment.creditLabel','Crédit :')} <strong style={{ color: '#f97316' }}>{currency(total - Number(paymentAmount || 0))}</strong></div>
                </div>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!paymentType ? (
            <Button variant="secondary" onClick={() => setShowPayment(false)}>{t('caisse.payment.cancel','Annuler')}</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setPaymentType('')}>{t('caisse.payment.back','Retour')}</Button>
              <Button variant="success" onClick={submitSale}>{t('caisse.payment.confirm','Confirmer')}</Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Add Client Modal */}
      <Modal show={showAddClient} onHide={() => setShowAddClient(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('caisse.addClient.title','Ajouter un client')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="clientName">
              <Form.Label>{t('caisse.addClient.labels.name','Nom')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('caisse.addClient.placeholders.name','Nom du client')}
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="clientICE">
              <Form.Label>{t('caisse.addClient.labels.ice','ICE')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("caisse.addClient.placeholders.ice","Identifiant Commun de l'Entreprise")}
                value={newClient.ice}
                onChange={(e) => setNewClient({ ...newClient, ice: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="clientCompany">
              <Form.Label>{t('caisse.addClient.labels.company','ENTREPRISE')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('caisse.addClient.placeholders.company',"Nom de l'entreprise")}
                value={newClient.Entreprise}
                onChange={(e) => setNewClient({ ...newClient, Entreprise: e.target.value })}
              />
            </Form.Group>
            {/* Credit removed from form */}
            <Form.Group className="mb-3" controlId="clientPhone">
              <Form.Label>{t('caisse.addClient.labels.phone','Téléphone')}</Form.Label>
              <Form.Control
                type="tel"
                placeholder={t('caisse.addClient.placeholders.phone','Numéro de téléphone')}
                value={newClient.phone}
                onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="clientAddress">
              <Form.Label>{t('caisse.addClient.labels.address','Adresse')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder={t('caisse.addClient.placeholders.address','Adresse')}
                value={newClient.address}
                onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
              />
            </Form.Group>
            {/* Status removed from form */}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddClient(false)} disabled={addingClient}>{t('caisse.addClient.cancel','Annuler')}</Button>
          <Button variant="primary" disabled={addingClient || !newClient.name?.trim()} onClick={async () => {
            try {
              setAddingClient(true);
              const token = localStorage.getItem('authToken');
              const payload = {
                Nom: newClient.name?.trim(),
                ICE: newClient.ice || null,
                Entreprise: newClient.Entreprise || null,
                Telephone: newClient.phone || null,
                Address: newClient.address || null,
                Credit: 0,
                status: 'active',
              };
              const res = await axios.post('https://plateformewmg.cloud/api/Gestion/clientgestions', payload, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
              const created = res.data?.data || res.data?.client || res.data;
              // Refresh clients and select the new one
              await fetchClients();
              const cname = (created?.Nom || created?.name || created?.Name || newClient.name || '').toLowerCase();
              const found = clients.find(c => (c.name || '').toLowerCase() === cname);
              if (found) setSelectedClient(found); else setSelectedClient({ id: created?.id || created?._id, name: created?.name || created?.Name || newClient.name });
              setShowAddClient(false);
              setNewClient({ name: '', phone: '', address: '', Entreprise: '' });
              Swal.fire(t('caisse.alerts.success','Succès'), t('caisse.alerts.clientAdded','Client ajouté'), 'success');
            } catch (err) {
              console.error(err);
              Swal.fire(t('caisse.alerts.error','Erreur'), t('caisse.alerts.addClientFailed','Ajout du client impossible'));
            } finally {
              setAddingClient(false);
            }
          }}>{addingClient ? t('caisse.addClient.saving','Enregistrement...') : t('caisse.addClient.add','Ajouter')}</Button>
        </Modal.Footer>
      </Modal>
    </AnimationRevealPage>
  );
};

export default Caisse;
